import React from 'react';
import PropTypes from 'prop-types';
import styles from './CircularProgress.module.scss';

const SIZE = 44;

/**
 * Animated CircularProgress component matching Material-UI CircularProgress behavior and aesthetics.
 */
const CircularProgress = ({
  size = 40,
  thickness = 3.6,
  color = 'primary',
  value = 0,
  variant = 'indeterminate',
  disableShrink = false,
  className = '',
  style = {},
  sx = {},
  ...rest
}) => {
  const isCustomColor =
    typeof color === 'string' &&
    (color.startsWith('#') ||
      color.startsWith('rgb') ||
      color.startsWith('hsl'));

  const colorClass = !isCustomColor && styles[`color-${color}`]
    ? styles[`color-${color}`]
    : styles['color-primary'];

  const rootClasses = [
    styles['circular-progress'],
    styles[variant],
    !isCustomColor ? colorClass : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const circleClasses = [
    styles.circle,
    styles[`circle-${variant}`],
    disableShrink ? styles['circle-disable-shrink'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Compute dimensions
  const formattedSize = typeof size === 'number' ? `${size}px` : size;
  const radius = (SIZE - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  const circleStyle = {};

  if (variant === 'determinate') {
    circleStyle.strokeDasharray = circumference.toFixed(3);
    circleStyle.strokeDashoffset = `${(
      ((100 - Math.min(Math.max(value, 0), 100)) / 100) *
      circumference
    ).toFixed(3)}px`;
  }

  const rootStyle = {
    width: formattedSize,
    height: formattedSize,
    ...(isCustomColor ? { color } : {}),
    ...sx,
    ...style,
  };

  return (
    <span
      className={rootClasses}
      style={rootStyle}
      role="progressbar"
      aria-valuenow={variant === 'determinate' ? Math.round(value) : undefined}
      aria-valuemin={variant === 'determinate' ? 0 : undefined}
      aria-valuemax={variant === 'determinate' ? 100 : undefined}
      {...rest}
    >
      <svg
        className={styles.svg}
        viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}
      >
        <circle
          className={circleClasses}
          style={circleStyle}
          cx={SIZE}
          cy={SIZE}
          r={radius}
          fill="none"
          strokeWidth={thickness}
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
};

CircularProgress.propTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  thickness: PropTypes.number,
  color: PropTypes.string,
  value: PropTypes.number,
  variant: PropTypes.oneOf(['indeterminate', 'determinate']),
  disableShrink: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  sx: PropTypes.object,
};

export default CircularProgress;
